// ServiceLad paid report: turns the stored report snapshot into what the
// summary page and sections show. Pure functions, no React, so tests can
// run them. Rules (same spirit as Caroat's report):
// - A check the provider did not return is "Not checked", never clear.
// - A zero count or missing field is hidden, not shown as a pass.
// - Nothing is inferred beyond what the records say.

const DAY = 86400000;
const RETEST_WINDOW_DAYS = 60;

function toDate(value) {
  if (!value) return null;
  // CheckCar sends "2025-07-30T00:00:00" with no zone; read it as UTC so a
  // UK summer-time browser does not show the day before.
  const text = typeof value === "string" && /^\d{4}-\d{2}-\d{2}T[\d:.]+$/.test(value.trim()) ? `${value.trim()}Z` : value;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isoDay(value) {
  const date = toDate(value);
  return date ? date.toISOString().slice(0, 10) : null;
}

// Case-insensitive field read; CheckCar mixes "DateOfTransaction" and "dateoftransaction".
export function pick(source, keys) {
  if (!source || typeof source !== "object") return null;
  const lower = new Map(Object.keys(source).map((key) => [key.toLowerCase(), key]));
  for (const key of keys) {
    const real = lower.get(key.toLowerCase());
    if (real === undefined) continue;
    const value = source[real];
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && !value.trim()) continue;
    return typeof value === "string" ? value.trim() : value;
  }
  return null;
}

export function formatReportDate(value) {
  const date = toDate(value);
  if (!date) return null;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

export function formatMiles(value) {
  return value == null ? null : `${new Intl.NumberFormat("en-GB").format(value)} mi`;
}

function plural(count, word, many = `${word}s`) {
  return `${count} ${count === 1 ? word : many}`;
}

// "1 year 9 months", "28 days". Used for countdowns and how long a plate was held.
export function spanLabel(from, to) {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end || end < start) return null;
  let months = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth());
  if (end.getUTCDate() < start.getUTCDate()) months -= 1;
  const years = Math.floor(months / 12);
  const rest = months % 12;
  if (years || rest) {
    return [years ? plural(years, "year") : null, rest ? plural(rest, "month") : null].filter(Boolean).join(" ");
  }
  return plural(Math.max(0, Math.round((end - start) / DAY)), "day");
}

/* ---------- MOT ---------- */

// Group tests into MOT cycles, oldest first: a fail followed within 60 days by
// another test is the same cycle (a retest), so a fail fixed on the day does
// not read as a failed year.
export function groupMotCycles(tests = []) {
  const ordered = tests
    .filter((test) => toDate(test.completed))
    .slice()
    .sort((a, b) => toDate(a.completed) - toDate(b.completed));
  const cycles = [];
  for (const test of ordered) {
    const current = cycles[cycles.length - 1];
    const last = current?.tests[current.tests.length - 1];
    const gapDays = last ? (toDate(test.completed) - toDate(last.completed)) / DAY : Infinity;
    if (last && last.result === "FAILED" && gapDays <= RETEST_WINDOW_DAYS) {
      current.tests.push(test);
    } else {
      cycles.push({ tests: [test] });
    }
  }
  return cycles.map((cycle) => {
    const final = cycle.tests[cycle.tests.length - 1];
    const first = cycle.tests[0];
    const outcome = final.result === "PASSED"
      ? (cycle.tests.length > 1 ? "retest" : "pass")
      : final.result === "FAILED" ? "fail" : "other";
    return { tests: cycle.tests, first, final, outcome };
  }).reverse();
}

export function buildMot(report, vehicle, now) {
  const tests = Array.isArray(report?.mot_history) ? report.mot_history : [];
  const cycles = groupMotCycles(tests);
  const counted = cycles.filter((cycle) => cycle.outcome !== "other");
  const firstTime = counted.filter((cycle) => cycle.outcome === "pass").length;
  const retests = counted.filter((cycle) => cycle.outcome === "retest").length;
  const failed = counted.filter((cycle) => cycle.outcome === "fail").length;
  const expiry = toDate(vehicle?.mot_expiry_date);
  const status = vehicle?.mot_status || null;
  const valid = status === "Valid" && expiry && expiry >= now;
  let defectCounts = { dangerous: 0, major: 0 };
  for (const test of tests) {
    for (const defect of test.defects || []) {
      if (defect.type === "DANGEROUS") defectCounts.dangerous += 1;
      if (defect.type === "MAJOR") defectCounts.major += 1;
    }
  }
  return {
    tests,
    cycles,
    stats: counted.length ? {
      cycles: counted.length,
      firstTime,
      retests,
      failed,
      firstTimeRate: Math.round((firstTime / counted.length) * 100),
    } : null,
    status,
    valid: Boolean(valid),
    // The snapshot can say "Valid" for an MOT that has run out since, as
    // reports stay open for weeks. Never show "Valid" once the date has passed.
    label: valid ? "Valid" : status === "Valid" ? "Expired" : status,
    expiry: expiry ? expiry.toISOString() : null,
    countdown: valid ? spanLabel(now, expiry) : null,
    defectCounts,
  };
}

/* ---------- Mileage ---------- */

export function buildMileage(report, { firstRegistered, imported }) {
  const readings = (report?.mot_history || [])
    .filter((test) => test.mileage != null && toDate(test.completed))
    .map((test) => ({ date: toDate(test.completed).toISOString(), miles: Number(test.mileage) }))
    .filter((point) => Number.isFinite(point.miles))
    .sort((a, b) => a.date.localeCompare(b.date));

  // One point per day: a fail and its same-day retest share a reading.
  const points = [];
  for (const reading of readings) {
    const last = points[points.length - 1];
    if (last && last.date.slice(0, 10) === reading.date.slice(0, 10)) {
      last.miles = Math.max(last.miles, reading.miles);
    } else {
      points.push({ ...reading, dropped: false });
    }
  }
  for (let i = 1; i < points.length; i += 1) {
    points[i].dropped = points[i].miles < points[i - 1].miles;
  }

  if (points.length < 2) return { points, available: false, last: points[0] || null };

  // A car registered new in the UK started at zero miles; an import did not.
  const start = firstRegistered && imported === false && firstRegistered < points[0].date
    ? { date: firstRegistered, miles: 0, start: true, dropped: false }
    : null;
  const chart = start ? [start, ...points] : points;
  const from = start || points[0];
  const last = points[points.length - 1];
  const years = (toDate(last.date) - toDate(from.date)) / (365.25 * DAY);
  const perYear = years >= 0.5 ? Math.round((last.miles - from.miles) / years) : null;
  const drops = points.filter((point) => point.dropped);

  return {
    available: true,
    points,
    chart,
    drops,
    alert: drops.length > 0,
    last,
    first: points[0],
    perYear,
  };
}

/* ---------- Finance ---------- */

function addMonths(iso, months) {
  const start = toDate(iso);
  if (!start) return null;
  return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + months, start.getUTCDate()));
}

export function normaliseFinance(item, now) {
  const started = isoDay(pick(item, ["DateOfAgreement", "AgreementDate", "StartDate", "Date"]));
  const termRaw = pick(item, ["AgreementTermInMonths", "AgreementTerm", "Term", "Duration"]);
  const months = termRaw != null ? Number(String(termRaw).replace(/[^0-9.]/g, "")) : NaN;
  const end = started && Number.isFinite(months) && months > 0 ? addMonths(started, Math.round(months)) : null;
  return {
    provider: pick(item, ["FinanceCompany", "CompanyName", "LenderName", "Provider"]),
    agreementType: pick(item, ["AgreementType", "FinanceType"]),
    agreementNumber: pick(item, ["AgreementNumber"]),
    contactNumber: pick(item, ["ContactNumber", "Telephone"]),
    started,
    term: Number.isFinite(months) && months > 0 ? `${Math.round(months)} months` : null,
    endsOn: end ? end.toISOString() : null,
    // No start or term means we cannot prove it ended, so it counts as running.
    outstanding: end ? end > now : true,
  };
}

/* ---------- Timeline ---------- */

function event(id, date, label, detail, tone = "neutral", kind = "other") {
  const day = isoDay(date);
  return day ? { id, date: day, label, detail: detail || null, tone, kind } : null;
}

export function buildTimeline({ firstRegistered, mot, rc, finance, writeoffs, plates }) {
  const events = [];
  if (firstRegistered) events.push(event("first", firstRegistered, "First registered", "First registration recorded", "neutral", "first"));

  for (const cycle of mot.cycles) {
    const date = cycle.final.completed;
    const miles = formatMiles(cycle.final.mileage);
    if (cycle.outcome === "retest") {
      const sameDay = isoDay(cycle.first.completed) === isoDay(date);
      const failedNote = sameDay ? "Failed, then passed the same day" : `Failed first on ${formatReportDate(cycle.first.completed)}`;
      events.push(event(`mot-${date}`, date, "MOT passed on retest", [failedNote, miles].filter(Boolean).join(" · "), "good", "mot"));
    } else if (cycle.outcome === "pass") {
      events.push(event(`mot-${date}`, date, "MOT passed", miles, "good", "mot"));
    } else if (cycle.outcome === "fail") {
      events.push(event(`mot-${date}`, date, "MOT failed", miles, "bad", "mot"));
    }
  }

  const firstDay = isoDay(firstRegistered);
  for (const [index, change] of (rc?.keepers?.changes || []).entries()) {
    const previous = Number(pick(change, ["NumberOfPreviousKeepers"]));
    const detail = Number.isFinite(previous) ? `${plural(previous, "previous keeper")} at this date` : null;
    events.push(event(`keeper-${index}`, pick(change, ["DateOfTransaction", "DateOfLastKeeperChange"]), "Keeper change", detail, "neutral", "keeper"));
  }
  for (const [index, cert] of (rc?.v5cCertificates || []).entries()) {
    const day = isoDay(pick(cert, ["CertificateDate", "Date"]));
    // The first logbook comes with first registration; listing it twice is noise.
    if (day && day !== firstDay) events.push(event(`v5c-${index}`, day, "V5C issued", "Logbook issued or replaced", "neutral", "v5c"));
  }
  for (const [index, plate] of plates.entries()) {
    events.push(event(`plate-${index}`, plate.date, "Plate change", plate.registration ? `Registration ${plate.registration}` : null, "neutral", "plate"));
  }
  for (const [index, entry] of finance.entries()) {
    events.push(event(`finance-${index}`, entry.started, "Finance agreement started", [entry.agreementType, entry.provider].filter(Boolean).join(" · "), "warn", "finance"));
  }
  for (const [index, entry] of writeoffs.entries()) {
    events.push(event(`writeoff-${index}`, entry.lossDate, "Insurance write-off", entry.categoryLabel || entry.status, "bad", "writeoff"));
  }
  if (rc?.scrapped?.exported) events.push(event("exported", rc.scrapped.dateExported, "Exported", null, "bad", "status"));
  if (rc?.scrapped?.status) events.push(event("scrapped", rc.scrapped.date, "Scrapped", null, "bad", "status"));

  const all = events.filter(Boolean).sort((a, b) => b.date.localeCompare(a.date));
  return { all, featured: pickFeatured(all) };
}

// Up to four big moments, oldest on the left: first registration, every
// write-off, the latest MOT outcome, the first keeper change after
// registration, then finance, plate change and logbook if room is left.
export function pickFeatured(events) {
  const latest = (kind) => events.find((item) => item.kind === kind);
  const first = latest("first");
  const writeoffs = events.filter((item) => item.kind === "writeoff");
  const keepers = events.filter((item) => item.kind === "keeper").slice().sort((a, b) => a.date.localeCompare(b.date));
  const keeper = keepers.find((item) => !first || item.date > first.date) || keepers[0];
  const pool = [first, ...writeoffs, latest("mot"), keeper, latest("finance"), latest("plate"), latest("v5c")].filter(Boolean);
  const unique = [];
  for (const item of pool) if (!unique.some((other) => other.id === item.id)) unique.push(item);
  return unique.slice(0, Math.max(4, 2 + writeoffs.length)).sort((a, b) => a.date.localeCompare(b.date));
}

/* ---------- Whole report ---------- */

export function buildSlReport(report, { vehicle, now = new Date() } = {}) {
  const rc = report?.risk_checks || null;
  const known = Boolean(rc?.available);
  const specs = report?.vehicle_specs || {};
  const vi = specs.VehicleIdentification || {};
  const firstRegistered = isoDay(vehicle?.first_used_date || vi.DateFirstRegisteredInUk || vi.DateFirstRegistered || rc?.scrapped?.dateFirstRegisteredUk);
  const imported = known && rc.scrapped ? rc.scrapped.imported === true : null;

  const mot = buildMot(report, vehicle, now);
  const mileage = buildMileage(report, { firstRegistered, imported });
  const finance = known ? (rc.finance || []).map((item) => normaliseFinance(item, now)) : [];
  const runningFinance = finance.filter((entry) => entry.outstanding);
  // financeRecord can be true with an empty list; that still means finance.
  const financeFound = known && (rc.hasFinance || finance.length > 0);
  const writeoffs = known ? rc.writeoffs || [] : [];
  const plates = known
    ? (rc.plateChanges || []).map((item) => ({
      registration: pick(item, ["CurrentVRM", "currentvrm", "PreviousVRM", "previousvrm", "Registration", "VRM"]),
      date: isoDay(pick(item, ["DateOfTransfer", "dateofchange", "DateOfChange", "DateOfTransaction", "Date"])),
    }))
    : [];
  const timeline = buildTimeline({ firstRegistered, mot, rc: known ? rc : null, finance, writeoffs, plates });
  const v5cDates = known
    ? [...new Set((rc.v5cCertificates || []).map((cert) => isoDay(pick(cert, ["CertificateDate", "Date"]))).filter(Boolean))].sort((a, b) => b.localeCompare(a))
    : [];
  const keepers = known && rc.keepers ? {
    total: rc.keepers.total,
    previous: rc.keepers.previous,
    lastChange: (rc.keepers.changes || [])
      .map((change) => isoDay(pick(change, ["DateOfTransaction", "DateOfLastKeeperChange"])))
      .filter(Boolean)
      .sort((a, b) => b.localeCompare(a))[0] || null,
  } : null;

  const notChecked = { value: "Not checked", tone: "muted" };
  const tax = vehicle?.tax_status || null;
  const recall = vehicle?.has_outstanding_recall;
  const scrapped = known && rc.scrapped ? rc.scrapped : null;

  const quick = [
    { id: "stolen", label: "Stolen", href: "#stolen", ...(!known ? notChecked : rc.stolen ? { value: "Marker found", tone: "bad" } : { value: "Clear", tone: "good" }) },
    { id: "writeoff", label: "Write-off", href: "#damage", ...(!known ? notChecked : writeoffs.length ? { value: `${writeoffs.length} found`, tone: "bad" } : { value: "Clear", tone: "good" }) },
    {
      id: "finance",
      label: "Finance",
      href: "#finance",
      ...(!known ? notChecked
        : runningFinance.length || (financeFound && !finance.length) ? { value: "Still running", tone: "bad" }
          : finance.length ? { value: `${finance.length} settled`, tone: "warn" }
            : { value: "Clear", tone: "good" }),
    },
    { id: "mileage", label: "Mileage", href: "#mileage", ...(!mileage.available ? notChecked : mileage.alert ? { value: "Drop found", tone: "bad" } : { value: "No drop", tone: "good" }) },
    { id: "scrapped", label: "Scrapped", href: "#status", ...(!scrapped ? notChecked : scrapped.status ? { value: "Yes", tone: "bad" } : { value: "No", tone: "good" }) },
    { id: "imported", label: "Imported", href: "#status", ...(!scrapped ? notChecked : scrapped.imported ? { value: "Yes", tone: "warn" } : { value: "No", tone: "good" }) },
    { id: "mot", label: "MOT", href: "#mot", ...(!mot.status ? notChecked : { value: mot.label, tone: mot.valid ? "good" : "bad" }) },
    { id: "tax", label: "Tax", href: "#tax", ...(!tax ? notChecked : { value: tax, tone: tax === "Taxed" ? "good" : tax === "SORN" ? "warn" : "warn" }) },
  ];

  // Things a buyer must look into before handing money over.
  const alerts = [];
  if (known && rc.stolen) alerts.push({ text: "Recorded as stolen", href: "#stolen" });
  for (const entry of writeoffs) alerts.push({ text: `Insurance write-off${entry.categoryLetter ? `, Category ${entry.categoryLetter}` : ""}${entry.lossDate ? ` (${formatReportDate(entry.lossDate)})` : ""}`, href: "#damage" });
  if (runningFinance.length || (financeFound && !finance.length)) alerts.push({ text: "Finance still running against this vehicle", href: "#finance" });
  if (mileage.alert) alerts.push({ text: "A mileage reading is lower than the one before", href: "#mileage" });
  if (scrapped?.status) alerts.push({ text: "Recorded as scrapped", href: "#status" });
  if (scrapped?.exported) alerts.push({ text: "Recorded as exported", href: "#status" });
  if (mot.status && !mot.valid) alerts.push({ text: mot.label === "Expired" ? "MOT has expired" : `MOT ${mot.status === "Failed" ? "failed" : "not valid"}`, href: "#mot" });
  if (recall === true) alerts.push({ text: "Outstanding safety recall", href: "#status" });

  // Worth knowing, not a warning.
  const notes = [];
  if (tax && tax !== "Taxed") {
    const taxDue = formatReportDate(vehicle?.tax_due_date);
    notes.push({ text: tax === "SORN" ? "Declared off the road (SORN)" : "Not taxed at the moment", detail: taxDue ? `(Expired ${taxDue})` : null, href: "#tax" });
  }
  if (scrapped?.imported) notes.push({ text: "Imported vehicle", href: "#status" });
  if (!runningFinance.length && finance.length) notes.push({ text: `${plural(finance.length, "finance agreement")} on record, now ended`, href: "#finance" });
  if (plates.length) notes.push({ text: `${plural(plates.length, "plate change")} recorded`, href: "#ownership" });

  const checkedOn = formatReportDate(report?.generated_at);
  const verdict = !known
    ? {
      tone: "muted",
      title: "History checks not available",
      text: "Stolen, write-off and finance records did not come back for this vehicle, so they are not shown as clear.",
    }
    : alerts.length
      ? {
        tone: "bad",
        title: alerts.length === 1 ? "1 thing needs a closer look" : `${alerts.length} things need a closer look`,
        text: "Read the sections below before you buy or sell.",
      }
      : {
        tone: "good",
        title: "No warning records found",
        text: `No stolen, write-off, running finance, scrapped or mileage warnings in the records checked${checkedOn ? ` on ${checkedOn}` : ""}.`,
      };

  const glance = [
    keepers?.total ? { label: keepers.total === 1 ? "keeper" : "keepers", value: keepers.total, href: "#ownership" } : null,
    plates.length ? { label: plates.length === 1 ? "plate change" : "plate changes", value: plates.length, href: "#ownership" } : null,
    v5cDates.length ? { label: v5cDates.length === 1 ? "logbook issued" : "logbooks issued", value: v5cDates.length, href: "#ownership" } : null,
    mot.stats ? { label: mot.stats.cycles === 1 ? "MOT year" : "MOT years", value: mot.stats.cycles, href: "#mot" } : null,
    mileage.last ? { label: "latest recorded miles", value: new Intl.NumberFormat("en-GB").format(mileage.last.miles), href: "#mileage" } : null,
  ].filter(Boolean).slice(0, 4);

  return {
    known,
    checkedOn,
    firstRegistered,
    quick,
    alerts,
    notes,
    verdict: { ...verdict, items: alerts },
    glance,
    mot,
    mileage,
    finance,
    runningFinance,
    financeFound,
    writeoffs,
    plates: plates
      .slice()
      .sort((a, b) => String(b.date).localeCompare(String(a.date)))
      .map((plate, index, list) => ({
        ...plate,
        current: index === 0,
        heldFor: plate.date ? spanLabel(plate.date, index === 0 ? now : list[index - 1].date) : null,
      })),
    timeline,
    keepers,
    v5cDates,
    stolen: known ? Boolean(rc.stolen) : null,
    scrapped,
    vic: known && rc.vic?.count > 0 ? rc.vic : null,
    recall: recall === true || recall === false ? recall : null,
  };
}
